/** Lightweight guided tour overlay for first-time users. */

import { useEffect, useMemo, useRef, useState } from "react";

import { useLanguageContext } from "../hooks/useLanguage";
import { useMediaQuery } from "../hooks/useMediaQuery";

export interface TourStep {
  id: string;
  targetId?: string;
  title: string;
  body: string;
  action?: string;
  isComplete?: boolean;
  placement?: "auto" | "top" | "bottom";
}

interface OnboardingTourProps {
  open: boolean;
  steps: TourStep[];
  stepIndex: number;
  firstStepIndex?: number;
  onStepChange: (index: number) => void;
  onClose: () => void;
  onComplete: () => void;
}

interface TargetRect {
  top: number;
  left: number;
  width: number;
  height: number;
  right: number;
  bottom: number;
}

interface ViewportSize {
  width: number;
  height: number;
}

const VIEWPORT_MARGIN = 16;
const TARGET_PADDING = 8;

function clamp(value: number, min: number, max: number): number {
  if (max < min) return min;
  return Math.min(Math.max(value, min), max);
}

function getViewportSize(): ViewportSize {
  if (typeof window === "undefined") return { width: 1024, height: 768 };
  return { width: window.innerWidth, height: window.innerHeight };
}

function getTargetRect(targetId?: string): TargetRect | null {
  if (!targetId || typeof document === "undefined") return null;
  const element = document.querySelector(`[data-tour-id="${targetId}"]`);
  if (!(element instanceof HTMLElement)) return null;
  const rect = element.getBoundingClientRect();
  if (rect.width === 0 || rect.height === 0) return null;
  return {
    top: rect.top,
    left: rect.left,
    width: rect.width,
    height: rect.height,
    right: rect.right,
    bottom: rect.bottom,
  };
}

export function OnboardingTour({
  open,
  steps,
  stepIndex,
  firstStepIndex = 0,
  onStepChange,
  onClose,
  onComplete,
}: OnboardingTourProps) {
  const { t } = useLanguageContext();
  const isMobile = useMediaQuery("(max-width: 639px)");
  const calloutRef = useRef<HTMLDivElement>(null);
  const [targetRect, setTargetRect] = useState<TargetRect | null>(null);
  const [viewport, setViewport] = useState<ViewportSize>(getViewportSize);
  const [calloutHeight, setCalloutHeight] = useState(240);
  const step = steps[stepIndex];
  const actionSatisfied = !step?.action || step.isComplete;
  const isLastStep = stepIndex >= steps.length - 1;
  const preferTop = step?.placement === "top";

  useEffect(() => {
    if (!open) return;
    const handleEscape = (event: KeyboardEvent) => {
      if (event.key === "Escape") onClose();
    };
    document.addEventListener("keydown", handleEscape);
    return () => document.removeEventListener("keydown", handleEscape);
  }, [onClose, open]);

  useEffect(() => {
    if (!open || !step) return;
    const reducedMotion = window.matchMedia?.("(prefers-reduced-motion: reduce)").matches;
    const target = step.targetId
      ? document.querySelector(`[data-tour-id="${step.targetId}"]`)
      : null;
    if (target instanceof HTMLElement) {
      target.scrollIntoView({
        block: "center",
        inline: "center",
        behavior: reducedMotion ? "auto" : "smooth",
      });
    }
  }, [open, step]);

  useEffect(() => {
    if (!open || !step) return;

    const updatePosition = () => {
      setViewport(getViewportSize());
      setTargetRect(getTargetRect(step.targetId));
    };

    updatePosition();
    const interval = window.setInterval(updatePosition, 400);
    window.addEventListener("resize", updatePosition);
    window.addEventListener("scroll", updatePosition, true);
    return () => {
      window.clearInterval(interval);
      window.removeEventListener("resize", updatePosition);
      window.removeEventListener("scroll", updatePosition, true);
    };
  }, [open, step]);

  useEffect(() => {
    if (!open) return;
    calloutRef.current?.focus();
    const measuredHeight = calloutRef.current?.offsetHeight;
    if (measuredHeight) setCalloutHeight(measuredHeight);
  }, [open, stepIndex, isMobile]);

  const calloutStyle = useMemo(() => {
    if (isMobile) return undefined;

    const width = Math.min(360, Math.max(280, viewport.width - VIEWPORT_MARGIN * 2));
    if (!targetRect) {
      return {
        left: clamp((viewport.width - width) / 2, VIEWPORT_MARGIN, viewport.width - width - VIEWPORT_MARGIN),
        top: clamp((viewport.height - calloutHeight) / 2, VIEWPORT_MARGIN, viewport.height - calloutHeight - VIEWPORT_MARGIN),
        width,
      };
    }

    const belowTop = targetRect.bottom + 14;
    const aboveTop = targetRect.top - calloutHeight - 14;
    const maxTop = Math.max(VIEWPORT_MARGIN, viewport.height - calloutHeight - VIEWPORT_MARGIN);
    const canFitAbove = aboveTop >= VIEWPORT_MARGIN;
    const canFitBelow = belowTop + calloutHeight <= viewport.height - VIEWPORT_MARGIN;
    const top = preferTop
      ? clamp(canFitAbove ? aboveTop : VIEWPORT_MARGIN, VIEWPORT_MARGIN, maxTop)
      : step?.placement === "bottom"
        ? clamp(canFitBelow ? belowTop : maxTop, VIEWPORT_MARGIN, maxTop)
        : canFitBelow || !canFitAbove
          ? clamp(belowTop, VIEWPORT_MARGIN, maxTop)
          : clamp(aboveTop, VIEWPORT_MARGIN, maxTop);
    const left = clamp(
      targetRect.left + targetRect.width / 2 - width / 2,
      VIEWPORT_MARGIN,
      Math.max(VIEWPORT_MARGIN, viewport.width - width - VIEWPORT_MARGIN),
    );
    return { left, top, width };
  }, [calloutHeight, isMobile, preferTop, step?.placement, targetRect, viewport]);

  const highlightStyle = useMemo(() => {
    if (!targetRect) return undefined;
    const left = clamp(targetRect.left - TARGET_PADDING, 4, viewport.width - 8);
    const top = clamp(targetRect.top - TARGET_PADDING, 4, viewport.height - 8);
    return {
      left,
      top,
      width: Math.min(targetRect.width + TARGET_PADDING * 2, viewport.width - left - 4),
      height: Math.min(targetRect.height + TARGET_PADDING * 2, viewport.height - top - 4),
    };
  }, [targetRect, viewport]);

  if (!open || !step) return null;

  const relativeStepIndex = Math.max(1, stepIndex - firstStepIndex + 1);
  const relativeStepCount = Math.max(1, steps.length - firstStepIndex);
  const progressText = `${t("tour_step")} ${relativeStepIndex} ${t("tour_of")} ${relativeStepCount}`;

  return (
    <div className="pointer-events-none fixed inset-0 z-[10000]" aria-live="polite">
      <div className="absolute inset-0 bg-flima-spruce/25" aria-hidden="true" />

      {highlightStyle ? (
        <div
          className="absolute rounded-2xl border-2 border-white bg-white/10 shadow-[0_0_0_9999px_rgba(40,58,61,0.22),0_0_0_4px_rgba(20,125,146,0.45)] ring-2 ring-flima-river"
          style={highlightStyle}
          aria-hidden="true"
        />
      ) : null}

      <div
        ref={calloutRef}
        role="dialog"
        aria-labelledby="onboarding-tour-title"
        aria-describedby="onboarding-tour-body"
        tabIndex={-1}
        style={calloutStyle}
        className={`pointer-events-auto border border-flima-spruce/10 bg-white text-flima-slate shadow-panel focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-flima-river ${
          isMobile
            ? preferTop
              ? "fixed inset-x-3 top-3 max-h-[42dvh] overflow-y-auto rounded-2xl p-4"
              : "flima-safe-pb fixed inset-x-3 bottom-3 max-h-[72dvh] overflow-y-auto rounded-2xl p-4"
            : "fixed max-h-[calc(100dvh-2rem)] overflow-y-auto rounded-2xl p-5"
        }`}
      >
        <div className="flex items-start justify-between gap-4">
          <p className="font-utility text-[0.62rem] uppercase tracking-[0.14em] text-flima-river">
            {progressText}
          </p>
          <button
            type="button"
            onClick={onClose}
            className="-mr-2 -mt-2 flex h-10 w-10 shrink-0 items-center justify-center rounded-md text-2xl leading-none text-flima-slate/55 transition-colors hover:bg-flima-mist hover:text-flima-spruce focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-flima-river motion-reduce:transition-none"
            aria-label={t("tour_close")}
          >
            <span aria-hidden="true">×</span>
          </button>
        </div>

        <h2 id="onboarding-tour-title" className="mt-2 text-pretty font-display text-xl font-semibold text-flima-spruce">
          {step.title}
        </h2>
        <p id="onboarding-tour-body" className="mt-2 text-sm leading-relaxed text-flima-slate/75">
          {step.body}
        </p>

        {step.action ? (
          <p
            className={`mt-3 rounded-lg px-3 py-2 text-sm font-semibold ${
              actionSatisfied
                ? "bg-flima-river/10 text-flima-spruce"
                : "bg-flima-mist text-flima-slate/75"
            }`}
          >
            {step.action}
          </p>
        ) : null}

        <div className="mt-5 flex flex-wrap items-center justify-between gap-2">
          <button
            type="button"
            onClick={onClose}
            className="min-h-11 rounded-lg px-3 text-sm font-semibold text-flima-slate/65 transition-colors hover:bg-flima-mist hover:text-flima-spruce focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-flima-river motion-reduce:transition-none"
          >
            {t("tour_skip")}
          </button>
          <div className="flex items-center gap-2">
            <button
              type="button"
              onClick={() => onStepChange(stepIndex - 1)}
              disabled={stepIndex <= firstStepIndex}
              className="min-h-11 rounded-lg border border-flima-spruce/15 bg-white px-3 text-sm font-semibold text-flima-slate transition-colors hover:bg-flima-mist focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-flima-river disabled:cursor-not-allowed disabled:opacity-45 motion-reduce:transition-none"
            >
              {t("tour_back")}
            </button>
            <button
              type="button"
              onClick={() => {
                if (isLastStep) onComplete();
                else onStepChange(stepIndex + 1);
              }}
              disabled={!actionSatisfied}
              className="min-h-11 rounded-lg bg-flima-spruce px-4 text-sm font-semibold text-white shadow-sm transition-colors hover:bg-flima-river focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-flima-river focus-visible:ring-offset-2 disabled:cursor-not-allowed disabled:opacity-55 motion-reduce:transition-none"
            >
              {isLastStep ? t("tour_finish") : t("tour_next")}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}
